import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Activity,
  AudioLines,
  BarChart3,
  CheckCircle2,
  Clock3,
  Plus,
  RefreshCw,
  RotateCcw,
  Save,
  Search,
  ShieldAlert,
} from "lucide-react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  XAxis,
  YAxis,
} from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  ChartLegend,
  ChartLegendContent,
  type ChartConfig,
} from "./ui/chart";
import { toast } from "sonner";
import { useHeaderActions } from "../context/HeaderActionsContext";
import { cn } from "../lib/utils";
import {
  apiService,
  type TtsPoolAccount,
  type TtsPoolEnvironmentMetrics,
  type TtsPoolMetrics,
} from "../services/api.service";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "./ui/card";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "./ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Switch } from "./ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";

const ALL_ENVIRONMENTS = "__all__";
const ALL_PROVIDERS = "__all_providers__";
const CURRENT_ENV_KEY = "__current__";
const DEFAULT_TTS_PROVIDER = "cartesia";
const AUTO_REFRESH_INTERVAL_MS = 5000;
const PROVIDER_OPTIONS = [
  { value: "cartesia", label: "Cartesia" },
  { value: "elevenlabs", label: "ElevenLabs" },
] as const;
const auditOutcomeChartConfig = {
  selections: {
    label: "Requests",
    color: "var(--chart-1)",
  },
  successes: {
    label: "Successful",
    color: "var(--chart-2)",
  },
  failures: {
    label: "Failed before audio",
    color: "var(--chart-3)",
  },
} satisfies ChartConfig;
const textareaClassName =
  "border-input placeholder:text-muted-foreground dark:bg-input/30 focus-visible:border-ring focus-visible:ring-ring/50 min-h-40 w-full rounded-md border bg-transparent px-3 py-2.5 font-mono text-[13px] leading-6 shadow-xs transition-[color,box-shadow] outline-none resize-y focus-visible:ring-[3px]";

type EditorState = {
  mode: "new" | "existing";
  originalAccountId: string | null;
  originalEnvironment: string;
  provider: string;
  accountId: string;
  environment: string;
  enabled: boolean;
  apiKey: string;
  voice: string;
  pronunciationDictId: string;
  voiceOverridesText: string;
  weight: string;
  priority: string;
};

type AccountStatusAppearance = {
  label: string;
  className: string;
};

type EditorSectionProps = {
  title: string;
  description: string;
  children: ReactNode;
  className?: string;
};

type FormFieldProps = {
  label: string;
  description?: string;
  className?: string;
  children: ReactNode;
};

type StatusMetricProps = {
  label: string;
  value: string;
  className?: string;
};

type SnapshotFieldProps = {
  label: string;
  value: string;
  className?: string;
};

type ProviderAuditDatum = {
  provider: string;
  providerLabel: string;
  selections: number;
  successes: number;
  failures: number;
};

type KeySplitDatum = {
  accountId: string;
  label: string;
  provider: string;
  selections: number;
  successes: number;
  failures: number;
  sharePct: number;
  successRatePct: number | null;
  weight: number | null;
};

type AuditSummaryStats = {
  selectionCount: number;
  successCount: number;
  beforeAudioFailureCount: number;
  failoverCount: number;
  fallbackToElevenlabsCount: number;
  successRatePct: number | null;
};

type ActiveTutorSummary = {
  totalActiveTutors: number;
  activeAccounts: number;
  providerBreakdownLabel: string;
};

// Used by colocated tests; cumulative labels stay explicit until real rolling windows exist.
// eslint-disable-next-line react-refresh/only-export-components
export const TTS_POOL_METRIC_LABELS = {
  traffic: "Selections",
  success: "Successes",
  failure: "Before-audio failures",
  failover: "Failovers",
  fallback: "ElevenLabs fallbacks",
} as const;

// Used by colocated tests; keep this export in-file to avoid widening the write scope.
// eslint-disable-next-line react-refresh/only-export-components
export function parseTtsPoolInteger(rawValue: string): {
  value?: number;
  error?: string;
} {
  const trimmed = rawValue.trim();
  if (!trimmed) {
    return { value: undefined };
  }

  if (!/^\d+$/.test(trimmed)) {
    return { error: "Weight must be a whole number" };
  }

  return { value: Number(trimmed) };
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function asNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

function asBoolean(value: unknown): boolean | null {
  return typeof value === "boolean" ? value : null;
}

function pickFirst<T>(
  source: Record<string, unknown>,
  keys: string[],
  parser: (value: unknown) => T | null,
): T | null {
  for (const key of keys) {
    const parsed = parser(source[key]);
    if (parsed !== null) return parsed;
  }
  return null;
}

function getEnvironment(account: TtsPoolAccount): string {
  return pickFirst(account, ["environment", "env"], asString) ?? "";
}

function getProvider(account: TtsPoolAccount): string {
  return pickFirst(account, ["provider"], asString) ?? DEFAULT_TTS_PROVIDER;
}

function getEnvironmentKey(environment: string): string {
  return environment || CURRENT_ENV_KEY;
}

function getAccountKey(account: TtsPoolAccount): string {
  return `${getEnvironmentKey(getEnvironment(account))}::${getProvider(account)}::${account.account_id}`;
}

function getMaskedApiKey(account: TtsPoolAccount): string | null {
  return pickFirst(
    account,
    ["api_key_masked", "masked_api_key", "api_key"],
    asString,
  );
}

function getVoice(account: TtsPoolAccount): string {
  return pickFirst(account, ["voice", "default_voice"], asString) ?? "";
}

function getPronunciationDictId(account: TtsPoolAccount): string {
  const value = pickFirst(account, ["pronunciation_dict_id"], asString);
  if (value !== null) return value;
  return "pronunciation_dict_id" in account ? "" : "";
}

function getWeight(account: TtsPoolAccount): number | null {
  return pickFirst(account, ["weight"], asNumber);
}

function getPriority(account: TtsPoolAccount): number | null {
  return pickFirst(account, ["priority"], asNumber);
}

function getSelectionCount(record: Record<string, unknown>): number {
  return (
    pickFirst(
      record,
      [
        "selection_count",
        "requests_24h",
        "request_count_24h",
        "requests_1h",
        "request_count_1h",
      ],
      asNumber,
    ) ?? 0
  );
}

function getSuccessCount(record: Record<string, unknown>): number {
  return pickFirst(record, ["success_count"], asNumber) ?? 0;
}

function getBeforeAudioFailureCount(record: Record<string, unknown>): number {
  return (
    pickFirst(
      record,
      [
        "before_audio_failure_count",
        "failures_24h",
        "failure_count_24h",
        "failures_1h",
        "failure_count_1h",
      ],
      asNumber,
    ) ?? 0
  );
}

function getFailoverCount(record: Record<string, unknown>): number {
  return (
    pickFirst(
      record,
      [
        "failover_count",
        "failovers_24h",
        "failover_count_24h",
        "failovers_1h",
        "failover_count_1h",
      ],
      asNumber,
    ) ?? 0
  );
}

function getFallbackToElevenlabsCount(record: Record<string, unknown>): number {
  return pickFirst(record, ["fallback_to_elevenlabs_count"], asNumber) ?? 0;
}

function getActiveTutorCount(record: Record<string, unknown>): number {
  return pickFirst(record, ["active_utterances", "active_tutors"], asNumber) ?? 0;
}

function getConsecutiveFailures(account: TtsPoolAccount): number {
  return pickFirst(account, ["consecutive_failures"], asNumber) ?? 0;
}

function getCooldownUntil(account: TtsPoolAccount): string | null {
  return pickFirst(
    account,
    ["cooldown_until", "cooldown_expires_at", "cooldown_ends_at"],
    asString,
  );
}

function isCoolingDown(account: TtsPoolAccount): boolean {
  const explicit = pickFirst(account, ["is_cooling_down"], asBoolean);
  if (explicit !== null) return explicit;

  const cooldownUntil = getCooldownUntil(account);
  if (!cooldownUntil) return false;
  const cooldownTime = new Date(cooldownUntil).getTime();
  return Number.isFinite(cooldownTime) && cooldownTime > Date.now();
}

function isHealthy(account: TtsPoolAccount): boolean | null {
  const explicit = pickFirst(account, ["healthy", "is_healthy"], asBoolean);
  if (explicit !== null) return explicit;
  if (isCoolingDown(account)) return false;
  return null;
}

function isEnabled(account: TtsPoolAccount): boolean {
  const explicit = pickFirst(account, ["enabled"], asBoolean);
  return explicit ?? true;
}

function getLastUsedAt(account: TtsPoolAccount): string | null {
  return pickFirst(account, ["last_used_at"], asString);
}

function getLastError(account: TtsPoolAccount): string | null {
  return pickFirst(account, ["last_error", "error"], asString);
}

function formatCount(value: number): string {
  return new Intl.NumberFormat("en-US").format(value);
}

function formatPercent(value: number | null): string {
  if (value === null || Number.isNaN(value)) return "No traffic yet";
  return `${value.toFixed(1)}%`;
}

type KeyPerformanceTooltipProps = {
  active?: boolean;
  payload?: Array<{
    payload?: KeySplitDatum;
  }>;
};

type ShareLabelProps = {
  x?: number | string;
  y?: number | string;
  width?: number | string;
  height?: number | string;
  value?: number | string;
};

function KeyPerformanceTooltip({ active, payload }: KeyPerformanceTooltipProps) {
  const chartDatum = payload?.[0]?.payload;

  if (!active || !chartDatum) {
    return null;
  }

  const outcomeRows = [
    {
      key: "selections",
      label: String(auditOutcomeChartConfig.selections.label),
      value: chartDatum.selections,
      color: "var(--chart-1)",
    },
    {
      key: "successes",
      label: String(auditOutcomeChartConfig.successes.label),
      value: chartDatum.successes,
      color: "var(--chart-2)",
    },
    {
      key: "failures",
      label: String(auditOutcomeChartConfig.failures.label),
      value: chartDatum.failures,
      color: "var(--chart-3)",
    },
  ] as const;

  return (
    <div className="border-border/50 bg-background grid min-w-[12rem] gap-2 rounded-lg border px-3 py-2.5 text-xs shadow-xl">
      <div className="font-medium">{chartDatum.label}</div>
      <div className="grid gap-1.5">
        {outcomeRows.map((row) => (
          <div
            key={row.key}
            className="flex items-center justify-between gap-3"
          >
            <div className="flex items-center gap-2 text-muted-foreground">
              <div
                className="h-2 w-2 shrink-0 rounded-[2px]"
                style={{ backgroundColor: row.color }}
              />
              <span>{row.label}</span>
            </div>
            <span className="text-foreground font-mono font-medium tabular-nums">
              {formatCount(row.value)}
            </span>
          </div>
        ))}
      </div>
      <div className="grid gap-1.5 border-t border-border/50 pt-2">
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Request share</span>
          <span className="text-foreground font-mono font-medium tabular-nums">
            {formatPercent(chartDatum.sharePct)}
          </span>
        </div>
        <div className="flex items-center justify-between gap-3">
          <span className="text-muted-foreground">Success rate</span>
          <span className="text-foreground font-mono font-medium tabular-nums">
            {formatPercent(chartDatum.successRatePct)}
          </span>
        </div>
      </div>
    </div>
  );
}

function KeyPerformanceShareLabel({
  x,
  y,
  width,
  height,
  value,
}: ShareLabelProps) {
  const xValue = Number(x);
  const yValue = Number(y);
  const widthValue = Number(width);
  const heightValue = Number(height);
  const sharePct =
    typeof value === "number"
      ? value
      : typeof value === "string"
        ? Number(value)
        : Number.NaN;

  if (
    !Number.isFinite(xValue) ||
    !Number.isFinite(yValue) ||
    !Number.isFinite(widthValue) ||
    !Number.isFinite(heightValue) ||
    !Number.isFinite(sharePct)
  ) {
    return null;
  }

  return (
    <text
      x={xValue + widthValue + 8}
      y={yValue + heightValue / 2}
      fill="var(--muted-foreground)"
      fontSize={11}
      textAnchor="start"
      dominantBaseline="middle"
    >
      {formatPercent(sharePct)}
    </text>
  );
}

function formatTimestamp(value: string | null): string {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function buildEditorState(
  account?: TtsPoolAccount,
  fallbackEnvironment: string = "",
): EditorState {
  const environment = account ? getEnvironment(account) : fallbackEnvironment;
  return {
    mode: account ? "existing" : "new",
    originalAccountId: account?.account_id ?? null,
    originalEnvironment: account ? getEnvironment(account) : environment,
    provider: account ? getProvider(account) : DEFAULT_TTS_PROVIDER,
    accountId: account?.account_id ?? "",
    environment,
    enabled: account ? isEnabled(account) : true,
    apiKey: "",
    voice: account ? getVoice(account) : "",
    pronunciationDictId: account ? getPronunciationDictId(account) : "",
    voiceOverridesText: account?.voice_overrides
      ? JSON.stringify(account.voice_overrides, null, 2)
      : "",
    weight:
      account && getWeight(account) != null ? String(getWeight(account)) : "",
    priority:
      account && getPriority(account) != null
        ? String(getPriority(account))
        : "",
  };
}

function getMetricsForEnvironment(
  metrics: TtsPoolMetrics | null,
  environment: string,
): TtsPoolMetrics | TtsPoolEnvironmentMetrics | null {
  if (!metrics) return null;
  if (environment === ALL_ENVIRONMENTS) return metrics;

  const scopedMetrics =
    metrics.by_environment?.find((item) => item.environment === environment) ??
    metrics.environments?.find((item) => item.environment === environment);
  if (scopedMetrics) return scopedMetrics;

  if (metrics.environment === environment) return metrics;
  return null;
}

function getProviderMetrics(
  metrics: TtsPoolMetrics | TtsPoolEnvironmentMetrics | null,
  provider: string,
): Record<string, unknown> | null {
  if (!metrics || provider === ALL_PROVIDERS) return metrics;

  const scopedMetrics =
    pickFirst(metrics, ["by_provider", "providers"], (value) =>
      Array.isArray(value) ? (value as Record<string, unknown>[]) : null,
    ) ?? [];

  return (
    scopedMetrics.find(
      (item) => (pickFirst(item, ["provider"], asString) ?? "") === provider,
    ) ?? null
  );
}

// Used by colocated tests; charts should stay pure and data-driven.
// eslint-disable-next-line react-refresh/only-export-components
export function buildProviderAuditChartData(
  metrics: TtsPoolMetrics | TtsPoolEnvironmentMetrics | null,
  selectedProvider: string = ALL_PROVIDERS,
): ProviderAuditDatum[] {
  if (!metrics) return [];

  const providerMetrics =
    pickFirst(metrics, ["by_provider", "providers"], (value) =>
      Array.isArray(value) ? (value as Record<string, unknown>[]) : null,
    ) ?? [];

  return providerMetrics
    .filter((record) => {
      const provider = pickFirst(record, ["provider"], asString) ?? "";
      return (
        selectedProvider === ALL_PROVIDERS || provider === selectedProvider
      );
    })
    .map((record) => {
      const provider = pickFirst(record, ["provider"], asString) ?? "unknown";
      return {
        provider,
        providerLabel: formatProviderLabel(provider),
        selections: getSelectionCount(record),
        successes: getSuccessCount(record),
        failures: getBeforeAudioFailureCount(record),
      };
    })
    .sort((left, right) =>
      left.providerLabel.localeCompare(right.providerLabel),
    );
}

// Used by colocated tests; keep the share calculation outside the component tree.
// eslint-disable-next-line react-refresh/only-export-components
export function buildKeySplitChartData(
  accounts: TtsPoolAccount[],
  selectedProvider: string = ALL_PROVIDERS,
): KeySplitDatum[] {
  const scopedAccounts = accounts.filter((account) => {
    return (
      selectedProvider === ALL_PROVIDERS ||
      getProvider(account) === selectedProvider
    );
  });
  const totalSelections = scopedAccounts.reduce(
    (sum, account) => sum + getSelectionCount(account),
    0,
  );

  return scopedAccounts
    .map((account) => {
      const selections = getSelectionCount(account);
      const successes = getSuccessCount(account);
      const failures = getBeforeAudioFailureCount(account);
      const provider = getProvider(account);
      return {
        accountId: account.account_id,
        label:
          selectedProvider === ALL_PROVIDERS
            ? `${formatProviderLabel(provider)} / ${account.account_id}`
            : account.account_id,
        provider,
        selections,
        successes,
        failures,
        sharePct:
          totalSelections > 0
            ? Number(((selections / totalSelections) * 100).toFixed(1))
            : 0,
        successRatePct:
          selections > 0
            ? Number(((successes / selections) * 100).toFixed(1))
            : null,
        weight: getWeight(account),
      };
    })
    .sort((left, right) => {
      if (right.selections !== left.selections) {
        return right.selections - left.selections;
      }
      return left.accountId.localeCompare(right.accountId);
    });
}

// Used by colocated tests; summary math should be testable without rendering.
// eslint-disable-next-line react-refresh/only-export-components
export function buildAuditSummaryStats(
  metrics: TtsPoolMetrics | TtsPoolEnvironmentMetrics | null,
  selectedProvider: string = ALL_PROVIDERS,
): AuditSummaryStats {
  const scopedMetrics = getProviderMetrics(metrics, selectedProvider) ?? {};
  const selectionCount = getSelectionCount(scopedMetrics);
  const successCount = getSuccessCount(scopedMetrics);
  const beforeAudioFailureCount = getBeforeAudioFailureCount(scopedMetrics);
  const failoverCount = getFailoverCount(scopedMetrics);
  const fallbackToElevenlabsCount = getFallbackToElevenlabsCount(scopedMetrics);

  return {
    selectionCount,
    successCount,
    beforeAudioFailureCount,
    failoverCount,
    fallbackToElevenlabsCount,
    successRatePct:
      selectionCount > 0
        ? Number(((successCount / selectionCount) * 100).toFixed(1))
        : null,
  };
}

// Used by colocated tests; the live-routing summary should stay pure.
// eslint-disable-next-line react-refresh/only-export-components
export function buildActiveTutorSummary(
  accounts: TtsPoolAccount[],
): ActiveTutorSummary {
  const providerCounts = new Map<string, number>();
  let totalActiveTutors = 0;
  let activeAccounts = 0;

  for (const account of accounts) {
    const activeTutors = getActiveTutorCount(account);
    if (activeTutors <= 0) continue;

    totalActiveTutors += activeTutors;
    activeAccounts += 1;

    const provider = getProvider(account);
    providerCounts.set(provider, (providerCounts.get(provider) ?? 0) + activeTutors);
  }

  const providerBreakdownLabel =
    providerCounts.size > 0
      ? Array.from(providerCounts.entries())
          .sort((left, right) => {
            if (right[1] !== left[1]) return right[1] - left[1];
            return left[0].localeCompare(right[0]);
          })
          .map(
            ([provider, count]) =>
              `${formatProviderLabel(provider)} ${formatCount(count)}`,
          )
          .join(" • ")
      : "No tutors live right now";

  return {
    totalActiveTutors,
    activeAccounts,
    providerBreakdownLabel,
  };
}

function formatProviderLabel(provider: string): string {
  const normalized = provider.trim().toLowerCase();
  if (normalized === "elevenlabs") return "ElevenLabs";
  if (normalized === "cartesia") return "Cartesia";
  return provider || "Unknown";
}

function formatEnvironmentLabel(environment: string): string {
  return environment || "Current env";
}

function getAccountStatusAppearance(
  account: TtsPoolAccount,
): AccountStatusAppearance {
  if (!isEnabled(account)) {
    return {
      label: "Disabled",
      className: "border-border bg-muted text-muted-foreground",
    };
  }

  if (isCoolingDown(account)) {
    return {
      label: "Cooling down",
      className: "border-amber-600/20 bg-amber-500/10 text-amber-900",
    };
  }

  if (isHealthy(account) === false) {
    return {
      label: "Needs attention",
      className: "border-destructive/20 bg-destructive/10 text-destructive",
    };
  }

  return {
    label: "Healthy",
    className: "border-lime-600/20 bg-lime-600/10 text-lime-900",
  };
}

function EditorSection({
  title,
  description,
  children,
  className,
}: EditorSectionProps) {
  return (
    <section
      className={cn(
        "rounded-lg border border-border/60 bg-background/75 p-4",
        className,
      )}
    >
      <div className="mb-3 space-y-1">
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      {children}
    </section>
  );
}

function FormField({
  label,
  description,
  className,
  children,
}: FormFieldProps) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="space-y-1">
        <Label className="text-sm font-medium text-foreground">{label}</Label>
        {description ? (
          <p className="text-xs leading-5 text-muted-foreground">
            {description}
          </p>
        ) : null}
      </div>
      {children}
    </div>
  );
}

function StatusMetric({ label, value, className }: StatusMetricProps) {
  return (
    <div
      className={cn(
        "rounded-lg border border-border/60 bg-background px-3 py-2.5",
        className,
      )}
    >
      <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-medium text-foreground">
        {value}
      </p>
    </div>
  );
}

function SnapshotField({ label, value, className }: SnapshotFieldProps) {
  return (
    <div
      className={cn(
        "rounded-md border border-border/60 bg-muted/20 px-3 py-2",
        className,
      )}
    >
      <p className="text-[10px] font-medium uppercase tracking-[0.16em] text-muted-foreground">
        {label}
      </p>
      <p className="mt-1 break-words text-sm font-medium text-foreground">
        {value}
      </p>
    </div>
  );
}

export const TtsPoolAdmin = () => {
  const { setActions } = useHeaderActions();
  const [accounts, setAccounts] = useState<TtsPoolAccount[]>([]);
  const [metrics, setMetrics] = useState<TtsPoolMetrics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(true);
  const [cooldownResettingFor, setCooldownResettingFor] = useState<
    string | null
  >(null);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<Date | null>(null);
  const [selectedEnvironment, setSelectedEnvironment] =
    useState<string>(ALL_ENVIRONMENTS);
  const [selectedAuditProvider, setSelectedAuditProvider] =
    useState<string>(ALL_PROVIDERS);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedAccountKey, setSelectedAccountKey] = useState<string | null>(
    null,
  );
  const [editor, setEditor] = useState<EditorState>(() => buildEditorState());

  const environmentOptions = useMemo(() => {
    return Array.from(
      new Set(
        accounts
          .map((account) => getEnvironment(account))
          .filter((environment) => environment.length > 0),
      ),
    ).sort((left, right) => left.localeCompare(right));
  }, [accounts]);

  const fallbackEnvironment = useMemo(() => {
    if (selectedEnvironment !== ALL_ENVIRONMENTS) return selectedEnvironment;
    return environmentOptions.length === 1 ? environmentOptions[0] : "";
  }, [environmentOptions, selectedEnvironment]);

  const scopedMetrics = useMemo(() => {
    return getMetricsForEnvironment(metrics, selectedEnvironment);
  }, [metrics, selectedEnvironment]);

  const loadData = useCallback(async (options?: { silent?: boolean }) => {
    if (options?.silent) {
      setIsRefreshing(true);
    } else {
      setIsLoading(true);
    }

    try {
      const [accountsResult, metricsResult] = await Promise.allSettled([
        apiService.getTtsPoolAccounts(),
        apiService.getTtsPoolMetrics(),
      ]);

      if (accountsResult.status === "rejected") {
        throw accountsResult.reason;
      }

      setAccounts(accountsResult.value);

      if (metricsResult.status === "fulfilled") {
        setMetrics(metricsResult.value);
      } else {
        console.error("Failed to load TTS pool metrics:", metricsResult.reason);
        setMetrics(null);
      }
      setLastRefreshedAt(new Date());
    } catch (error) {
      console.error("Failed to load TTS pool data:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to load TTS pool data",
      );
    } finally {
      if (options?.silent) {
        setIsRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!autoRefreshEnabled) return;

    const intervalId = window.setInterval(() => {
      if (
        document.visibilityState !== "visible" ||
        isLoading ||
        isRefreshing ||
        isSaving ||
        cooldownResettingFor
      ) {
        return;
      }

      void loadData({ silent: true });
    }, AUTO_REFRESH_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [
    autoRefreshEnabled,
    cooldownResettingFor,
    isLoading,
    isRefreshing,
    isSaving,
    loadData,
  ]);

  useEffect(() => {
    if (
      selectedEnvironment !== ALL_ENVIRONMENTS &&
      selectedEnvironment !== "" &&
      !environmentOptions.includes(selectedEnvironment)
    ) {
      setSelectedEnvironment(ALL_ENVIRONMENTS);
    }
  }, [environmentOptions, selectedEnvironment]);

  const envScopedAccounts = useMemo(() => {
    if (selectedEnvironment === ALL_ENVIRONMENTS) return accounts;
    return accounts.filter(
      (account) => getEnvironment(account) === selectedEnvironment,
    );
  }, [accounts, selectedEnvironment]);

  const auditProviderOptions = useMemo(() => {
    return Array.from(
      new Set(envScopedAccounts.map((account) => getProvider(account))),
    ).sort((left, right) => left.localeCompare(right));
  }, [envScopedAccounts]);

  useEffect(() => {
    if (
      selectedAuditProvider !== ALL_PROVIDERS &&
      !auditProviderOptions.includes(selectedAuditProvider)
    ) {
      setSelectedAuditProvider(ALL_PROVIDERS);
    }
  }, [auditProviderOptions, selectedAuditProvider]);

  const providerAuditChartData = useMemo(() => {
    return buildProviderAuditChartData(scopedMetrics, selectedAuditProvider);
  }, [scopedMetrics, selectedAuditProvider]);

  const keySplitChartData = useMemo(() => {
    return buildKeySplitChartData(envScopedAccounts, selectedAuditProvider);
  }, [envScopedAccounts, selectedAuditProvider]);

  const auditSummaryStats = useMemo(() => {
    return buildAuditSummaryStats(scopedMetrics, selectedAuditProvider);
  }, [scopedMetrics, selectedAuditProvider]);

  const visibleAccounts = useMemo(() => {
    const normalizedSearch = searchQuery.trim().toLowerCase();
    return [...envScopedAccounts]
      .filter((account) => {
        if (!normalizedSearch) return true;
        const maskedKey = getMaskedApiKey(account)?.toLowerCase() ?? "";
        const environment = getEnvironment(account).toLowerCase();
        const provider = getProvider(account).toLowerCase();
        return (
          account.account_id.toLowerCase().includes(normalizedSearch) ||
          maskedKey.includes(normalizedSearch) ||
          environment.includes(normalizedSearch) ||
          provider.includes(normalizedSearch)
        );
      })
      .sort((left, right) => {
        if (isEnabled(left) !== isEnabled(right)) {
          return Number(isEnabled(right)) - Number(isEnabled(left));
        }

        const priorityDelta =
          (getPriority(left) ?? Number.MAX_SAFE_INTEGER) -
          (getPriority(right) ?? Number.MAX_SAFE_INTEGER);
        if (priorityDelta !== 0) return priorityDelta;

        const environmentDelta = getEnvironment(left).localeCompare(
          getEnvironment(right),
        );
        if (environmentDelta !== 0) return environmentDelta;

        const providerDelta = getProvider(left).localeCompare(
          getProvider(right),
        );
        if (providerDelta !== 0) return providerDelta;

        return left.account_id.localeCompare(right.account_id);
      });
  }, [envScopedAccounts, searchQuery]);

  const hasActiveFilters =
    searchQuery.trim().length > 0 || selectedEnvironment !== ALL_ENVIRONMENTS;

  const selectedAccount = useMemo(() => {
    return accounts.find(
      (account) => getAccountKey(account) === selectedAccountKey,
    );
  }, [accounts, selectedAccountKey]);

  useEffect(() => {
    if (visibleAccounts.length === 0) {
      if (editor.mode !== "new") {
        setSelectedAccountKey(null);
        setEditor(buildEditorState(undefined, fallbackEnvironment));
      }
      return;
    }

    if (editor.mode === "new") return;

    if (
      selectedAccountKey &&
      visibleAccounts.some(
        (account) => getAccountKey(account) === selectedAccountKey,
      )
    ) {
      return;
    }

    const nextAccount = visibleAccounts[0];
    if (!nextAccount) return;
    setSelectedAccountKey(getAccountKey(nextAccount));
    setEditor(buildEditorState(nextAccount, fallbackEnvironment));
  }, [editor.mode, fallbackEnvironment, selectedAccountKey, visibleAccounts]);

  useEffect(() => {
    if (!selectedAccount || editor.mode === "new") return;
    setEditor(buildEditorState(selectedAccount, fallbackEnvironment));
  }, [fallbackEnvironment, selectedAccount, editor.mode]);

  const activeTutorSummary = useMemo(() => {
    return buildActiveTutorSummary(envScopedAccounts);
  }, [envScopedAccounts]);

  const selectedAccountStatus = selectedAccount
    ? getAccountStatusAppearance(selectedAccount)
    : null;
  const selectedAccountActiveTutors = selectedAccount
    ? getActiveTutorCount(selectedAccount)
    : 0;
  const lastRefreshedLabel = lastRefreshedAt
    ? lastRefreshedAt.toLocaleTimeString("en-US", {
        hour: "numeric",
        minute: "2-digit",
        second: "2-digit",
      })
    : null;

  const editorProviderLabel = formatProviderLabel(
    selectedAccount ? getProvider(selectedAccount) : editor.provider,
  );
  const editorEnvironmentLabel = formatEnvironmentLabel(
    selectedAccount ? getEnvironment(selectedAccount) : editor.environment,
  );
  const editorMaskedKeySummary = selectedAccount
    ? (getMaskedApiKey(selectedAccount) ?? "Stored")
    : editor.apiKey.trim()
      ? "Will be saved"
      : "Not set";
  const editorVoiceSummary = selectedAccount
    ? getVoice(selectedAccount) || "Not set"
    : editor.voice.trim() || "Not set";
  const editorWeightSummary = selectedAccount
    ? getWeight(selectedAccount) != null
      ? String(getWeight(selectedAccount))
      : null
    : editor.weight.trim() || null;
  const editorPrioritySummary = selectedAccount
    ? getPriority(selectedAccount) != null
      ? String(getPriority(selectedAccount))
      : null
    : editor.priority.trim() || null;

  const handleClearFilters = () => {
    setSearchQuery("");
    setSelectedEnvironment(ALL_ENVIRONMENTS);
  };

  useEffect(() => {
    setActions(
      <div className="flex items-center gap-2">
        <Badge
          variant="outline"
          className="rounded-full px-3 py-1 text-xs text-muted-foreground"
        >
          {formatCount(envScopedAccounts.length)} accounts
        </Badge>
        <Badge
          variant="outline"
          className="rounded-full px-3 py-1 text-xs text-muted-foreground"
        >
          {formatCount(activeTutorSummary.totalActiveTutors)} live
        </Badge>
        <Badge
          variant="outline"
          className="rounded-full px-3 py-1 text-xs text-muted-foreground"
        >
          {formatCount(auditSummaryStats.selectionCount)} selections
        </Badge>
        {selectedEnvironment !== ALL_ENVIRONMENTS ? (
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-xs text-muted-foreground"
          >
            {selectedEnvironment}
          </Badge>
        ) : null}
        {lastRefreshedLabel ? (
          <Badge
            variant="outline"
            className="rounded-full px-3 py-1 text-xs text-muted-foreground"
          >
            Updated {lastRefreshedLabel}
          </Badge>
        ) : null}
        <div className="flex items-center gap-2 rounded-full border border-border/60 px-3 py-1 text-xs text-muted-foreground">
          <span>{`Live ${AUTO_REFRESH_INTERVAL_MS / 1000}s`}</span>
          <Switch
            checked={autoRefreshEnabled}
            onCheckedChange={setAutoRefreshEnabled}
            aria-label="Toggle live refresh for TTS pool data"
          />
        </div>
        <Button
          variant="outline"
          size="icon"
          onClick={() => loadData({ silent: true })}
          disabled={isRefreshing}
          title="Refresh TTS pool data"
          className="h-8 w-8"
        >
          <RefreshCw
            className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
          />
        </Button>
      </div>,
    );

    return () => setActions(null);
  }, [
    envScopedAccounts.length,
    activeTutorSummary.totalActiveTutors,
    auditSummaryStats.selectionCount,
    autoRefreshEnabled,
    isRefreshing,
    lastRefreshedLabel,
    loadData,
    selectedEnvironment,
    setActions,
  ]);

  const handleSelectAccount = (account: TtsPoolAccount) => {
    setSelectedAccountKey(getAccountKey(account));
    setEditor(buildEditorState(account, fallbackEnvironment));
  };

  const handleCreateAccount = () => {
    setSelectedAccountKey(null);
    setEditor(buildEditorState(undefined, fallbackEnvironment));
  };

  const handleSave = async () => {
    const accountId = editor.accountId.trim();
    if (!accountId) {
      toast.error("Account ID is required");
      return;
    }
    const provider = editor.provider.trim().toLowerCase();
    if (!provider) {
      toast.error("Provider is required");
      return;
    }

    let voiceOverrides: Record<string, unknown> | undefined;
    const voiceOverridesText = editor.voiceOverridesText.trim();
    if (voiceOverridesText) {
      try {
        const parsed = JSON.parse(voiceOverridesText) as unknown;
        if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
          toast.error("Voice overrides must be a JSON object");
          return;
        }
        voiceOverrides = parsed as Record<string, unknown>;
      } catch {
        toast.error("Voice overrides must be valid JSON");
        return;
      }
    }

    const { value: weight, error: weightError } = parseTtsPoolInteger(
      editor.weight,
    );
    if (weightError) {
      toast.error(weightError);
      return;
    }

    const priority = editor.priority.trim()
      ? Number(editor.priority)
      : undefined;
    if (priority != null && !Number.isFinite(priority)) {
      toast.error("Priority must be a valid number");
      return;
    }

    try {
      setIsSaving(true);

      const response = await apiService.updateTtsPoolAccount(accountId, {
        provider,
        enabled: editor.enabled,
        api_key: editor.apiKey.trim() || undefined,
        voice: editor.voice.trim() || undefined,
        pronunciation_dict_id: editor.pronunciationDictId.trim(),
        voice_overrides: voiceOverrides,
        weight,
        priority,
        environment: editor.environment.trim() || undefined,
      });

      const nextEnvironment =
        response && getEnvironment(response)
          ? getEnvironment(response)
          : editor.environment.trim();
      const nextProvider =
        response && getProvider(response) ? getProvider(response) : provider;
      const nextKey = `${getEnvironmentKey(nextEnvironment)}::${nextProvider}::${accountId}`;

      toast.success(
        editor.mode === "new"
          ? "TTS pool account created"
          : "TTS pool account updated",
      );

      setSelectedAccountKey(nextKey);
      await loadData({ silent: true });
    } catch (error) {
      console.error("Failed to save TTS pool account:", error);
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save TTS pool account",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetCooldown = async () => {
    const accountId = editor.originalAccountId;
    if (!accountId) return;
    const provider = selectedAccount
      ? getProvider(selectedAccount)
      : editor.provider.trim().toLowerCase();
    const resetKey = selectedAccount ? getAccountKey(selectedAccount) : null;

    try {
      setCooldownResettingFor(resetKey ?? accountId);
      await apiService.resetTtsPoolCooldown(accountId, provider || undefined);
      toast.success("Cooldown cleared");
      await loadData({ silent: true });
    } catch (error) {
      console.error("Failed to reset TTS cooldown:", error);
      toast.error(
        error instanceof Error ? error.message : "Failed to reset TTS cooldown",
      );
    } finally {
      setCooldownResettingFor(null);
    }
  };

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
        Loading TTS pool data...
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-y-auto pr-1">
      <Tabs
        defaultValue="accounts"
        className="flex min-h-0 flex-1 flex-col gap-4"
      >
        <TabsList className="w-fit shrink-0 rounded-lg">
          <TabsTrigger value="accounts">Routing & Accounts</TabsTrigger>
          <TabsTrigger value="audit">Audit & Metrics</TabsTrigger>
        </TabsList>

        <TabsContent
          value="accounts"
          className="m-0 flex min-h-0 flex-1 flex-col gap-4 data-[state=inactive]:hidden"
        >
          <div className="grid min-h-0 flex-1 gap-4 xl:grid-cols-[minmax(420px,480px)_minmax(0,1fr)]">
            {/* Account List */}
            <div className="flex min-h-0 flex-col gap-3">
              {/* Filters */}
              <div className="shrink-0 flex flex-col gap-3 rounded-lg border border-border/60 bg-background/75 p-3">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Search account, provider, or key"
                      className="pl-9"
                    />
                  </div>
                  <Select
                    value={selectedEnvironment}
                    onValueChange={setSelectedEnvironment}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="All environments" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_ENVIRONMENTS}>
                        All environments
                      </SelectItem>
                      {environmentOptions.map((environment) => (
                        <SelectItem key={environment} value={environment}>
                          {environment}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button size="sm" onClick={handleCreateAccount}>
                    <Plus className="size-4" />
                    Add
                  </Button>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {hasActiveFilters
                      ? `Showing ${formatCount(visibleAccounts.length)} of ${formatCount(envScopedAccounts.length)}`
                      : `${formatCount(visibleAccounts.length)} accounts`}
                  </span>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleClearFilters}
                      className="h-7 px-2 text-xs"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>

              {/* Account Table */}
              <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-border/60">
                {visibleAccounts.length === 0 ? (
                  <div className="flex h-full items-center justify-center p-6 text-center">
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {hasActiveFilters
                          ? "No matching TTS pool accounts"
                          : "No TTS pool accounts yet"}
                      </p>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {hasActiveFilters
                          ? "Try clearing the search or widening the environment filter."
                          : "Add the first Cartesia or ElevenLabs account to start routing."}
                      </p>
                      <div className="mt-4 flex justify-center gap-2">
                        {hasActiveFilters && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleClearFilters}
                          >
                            Clear filters
                          </Button>
                        )}
                        <Button size="sm" onClick={handleCreateAccount}>
                          <Plus className="size-4" />
                          Add account
                        </Button>
                      </div>
                    </div>
                  </div>
                ) : (
                  <Table className="min-w-[560px]">
                    <TableHeader>
                      <TableRow className="hover:bg-transparent">
                        <TableHead className="w-[36%] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          Account
                        </TableHead>
                        <TableHead className="w-[16%] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          Provider
                        </TableHead>
                        <TableHead className="w-[14%] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          Active tutors
                        </TableHead>
                        <TableHead className="w-[14%] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          Traffic
                        </TableHead>
                        <TableHead className="w-[20%] px-3 py-2.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                          Status
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {visibleAccounts.map((account) => {
                        const active =
                          getAccountKey(account) === selectedAccountKey;
                        const status = getAccountStatusAppearance(account);
                        const providerLabel = formatProviderLabel(
                          getProvider(account),
                        );
                        const activeTutors = getActiveTutorCount(account);
                        return (
                          <TableRow
                            key={getAccountKey(account)}
                            data-state={active ? "selected" : undefined}
                            aria-selected={active}
                            tabIndex={0}
                            onClick={() => handleSelectAccount(account)}
                            onKeyDown={(event) => {
                              if (
                                event.key === "Enter" ||
                                event.key === " "
                              ) {
                                event.preventDefault();
                                handleSelectAccount(account);
                              }
                            }}
                            className={cn(
                              "cursor-pointer border-b border-border/40 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/40",
                              active ? "bg-muted/60" : "",
                            )}
                          >
                            <TableCell className="px-3 py-2.5">
                              <div className="flex flex-col gap-0.5">
                                <span className="text-sm font-medium text-foreground">
                                  {account.account_id}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                  {getMaskedApiKey(account) ?? "Key hidden"}
                                </span>
                              </div>
                            </TableCell>

                            <TableCell className="px-3 py-2.5">
                              <Badge
                                variant="outline"
                                className="rounded-full px-2 py-0.5 text-[11px]"
                              >
                                {providerLabel}
                              </Badge>
                            </TableCell>

                            <TableCell className="px-3 py-2.5">
                              <div className="flex flex-col gap-0.5">
                                <span
                                  className={cn(
                                    "text-sm font-medium",
                                    activeTutors > 0
                                      ? "text-foreground"
                                      : "text-muted-foreground",
                                  )}
                                >
                                  {formatCount(activeTutors)}
                                </span>
                                <span className="text-[11px] text-muted-foreground">
                                  {activeTutors > 0 ? "live now" : "idle"}
                                </span>
                              </div>
                            </TableCell>

                            <TableCell className="px-3 py-2.5">
                              <div className="flex flex-col gap-0.5 text-[11px] text-muted-foreground">
                                <span>
                                  {formatCount(getSelectionCount(account))} sel
                                </span>
                                <span>
                                  {formatCount(getSuccessCount(account))} ok
                                </span>
                              </div>
                            </TableCell>

                            <TableCell className="px-3 py-2.5">
                              <div className="flex flex-col gap-1">
                                <Badge
                                  className={cn(
                                    "w-fit rounded-full px-2 py-0.5 text-[11px]",
                                    status.className,
                                  )}
                                >
                                  {status.label}
                                </Badge>
                                <span className="text-[11px] text-muted-foreground">
                                  {isCoolingDown(account)
                                    ? "Cooldown"
                                    : isEnabled(account)
                                      ? "Enabled"
                                      : "Disabled"}
                                </span>
                              </div>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </div>
            </div>

            {/* Account Editor */}
            <Card className="flex min-h-0 flex-col overflow-hidden py-0">
              <CardHeader className="shrink-0 border-b border-border/60 px-4 py-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <CardTitle className="text-sm font-semibold">
                      {editor.mode === "new" ? "New Account" : "Edit Account"}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {editor.mode === "new"
                        ? "Create a new TTS pool account"
                        : `Editing ${editor.accountId}`}
                    </CardDescription>
                  </div>
                  <Badge
                    variant="outline"
                    className="rounded-full px-2.5 py-0.5 text-[11px] text-muted-foreground"
                  >
                    {editor.mode === "new" ? "New" : "Editing"}
                  </Badge>
                </div>
              </CardHeader>

              <CardContent className="min-h-0 flex-1 overflow-y-auto px-4 py-4">
                <div className="grid gap-6 xl:grid-cols-[1fr_280px]">
                  {/* Form */}
                  <div className="space-y-5">
                    <div className="space-y-3">
                      <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                        Identity
                      </h3>
                      <div className="grid gap-3 sm:grid-cols-3">
                        <FormField label="Account ID">
                          <Input
                            value={editor.accountId}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                accountId: event.target.value,
                              }))
                            }
                            placeholder="tts-account-01"
                            disabled={editor.mode === "existing"}
                          />
                        </FormField>

                        <FormField label="Provider">
                          <Select
                            value={editor.provider}
                            onValueChange={(value) =>
                              setEditor((current) => ({
                                ...current,
                                provider: value,
                              }))
                            }
                            disabled={editor.mode === "existing"}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select provider" />
                            </SelectTrigger>
                            <SelectContent>
                              {PROVIDER_OPTIONS.map((provider) => (
                                <SelectItem
                                  key={provider.value}
                                  value={provider.value}
                                >
                                  {provider.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </FormField>

                        <FormField label="Environment">
                          <Input
                            value={editor.environment}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                environment: event.target.value,
                              }))
                            }
                            placeholder="dev / stage / prod"
                          />
                        </FormField>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                        Credentials & Routing
                      </h3>
                      <div className="grid gap-3 sm:grid-cols-2">
                        <FormField label="API key">
                          <Input
                            type="password"
                            value={editor.apiKey}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                apiKey: event.target.value,
                              }))
                            }
                            placeholder={
                              editor.provider === "elevenlabs"
                                ? "Paste the ElevenLabs key"
                                : "Paste the Cartesia key"
                            }
                          />
                        </FormField>

                        <FormField label="Default voice">
                          <Input
                            value={editor.voice}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                voice: event.target.value,
                              }))
                            }
                            placeholder="Voice name or id"
                          />
                        </FormField>

                        <FormField
                          label="Pronunciation dict"
                          description={
                            editor.provider === "cartesia"
                              ? "Leave blank to disable for this account."
                              : "Not used by ElevenLabs."
                          }
                        >
                          <Input
                            value={editor.pronunciationDictId}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                pronunciationDictId: event.target.value,
                              }))
                            }
                            placeholder={
                              editor.provider === "cartesia"
                                ? "dict_..."
                                : "Not used"
                            }
                          />
                        </FormField>

                        <FormField label="Weight">
                          <Input
                            value={editor.weight}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                weight: event.target.value,
                              }))
                            }
                            inputMode="numeric"
                            placeholder="15"
                          />
                        </FormField>

                        <FormField label="Priority">
                          <Input
                            value={editor.priority}
                            onChange={(event) =>
                              setEditor((current) => ({
                                ...current,
                                priority: event.target.value,
                              }))
                            }
                            inputMode="numeric"
                            placeholder="10"
                          />
                        </FormField>
                      </div>

                      <div className="flex items-center justify-between rounded-md border border-border/60 bg-background px-3 py-2.5">
                        <span className="text-sm text-muted-foreground">
                          Enabled
                        </span>
                        <Switch
                          checked={editor.enabled}
                          onCheckedChange={(checked) =>
                            setEditor((current) => ({
                              ...current,
                              enabled: checked,
                            }))
                          }
                        />
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                        Voice Overrides
                      </h3>
                      <textarea
                        value={editor.voiceOverridesText}
                        onChange={(event) =>
                          setEditor((current) => ({
                            ...current,
                            voiceOverridesText: event.target.value,
                          }))
                        }
                        className={textareaClassName}
                        placeholder='{"en-US": "voice-primary"}'
                        spellCheck={false}
                      />
                    </div>

                    <div className="flex items-center justify-between gap-3 pt-2">
                      <p className="text-xs text-muted-foreground">
                        {editor.mode === "new"
                          ? "New accounts join the pool after save."
                          : "Changes apply after save."}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={handleSave}
                          disabled={isSaving}
                        >
                          <Save className="size-4" />
                          {isSaving ? "Saving..." : "Save"}
                        </Button>
                        {editor.mode === "existing" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleResetCooldown}
                            disabled={
                              cooldownResettingFor ===
                              (selectedAccount
                                ? getAccountKey(selectedAccount)
                                : editor.originalAccountId)
                            }
                          >
                            <RotateCcw className="size-4" />
                            Reset
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Sidebar info */}
                  <div className="space-y-4">
                    {selectedAccount && (
                      <div className="rounded-md border border-border/60 bg-background p-3">
                        <div className="mb-3 flex items-center justify-between">
                          <span className="text-xs font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                            Account Info
                          </span>
                          {selectedAccountStatus && (
                            <Badge
                              className={cn(
                                "rounded-full px-2 py-0.5 text-[11px]",
                                selectedAccountStatus.className,
                              )}
                            >
                              {selectedAccountStatus.label}
                            </Badge>
                          )}
                        </div>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Provider
                            </span>
                            <span className="font-medium">
                              {editorProviderLabel}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Environment
                            </span>
                            <span className="font-medium">
                              {editorEnvironmentLabel}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Last used
                            </span>
                            <span className="font-medium">
                              {formatTimestamp(
                                getLastUsedAt(selectedAccount),
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Active tutors
                            </span>
                            <span
                              className={cn(
                                "font-medium",
                                selectedAccountActiveTutors > 0
                                  ? "text-foreground"
                                  : "text-muted-foreground",
                              )}
                            >
                              {formatCount(selectedAccountActiveTutors)}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Enabled
                            </span>
                            <span className="font-medium">
                              {editor.enabled ? "Yes" : "No"}
                            </span>
                          </div>
                          <div className="flex justify-between gap-3 text-sm">
                            <span className="text-muted-foreground">
                              Pronunciation dict
                            </span>
                            <span className="max-w-[11rem] truncate text-right font-medium">
                              {getPronunciationDictId(selectedAccount) || "Off"}
                            </span>
                          </div>
                        </div>

                        <div className="mt-3 space-y-2 border-t border-border/40 pt-3">
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Selections
                            </span>
                            <span className="font-medium">
                              {formatCount(
                                getSelectionCount(selectedAccount),
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Successes
                            </span>
                            <span className="font-medium">
                              {formatCount(getSuccessCount(selectedAccount))}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Failures
                            </span>
                            <span className="font-medium">
                              {formatCount(
                                getBeforeAudioFailureCount(selectedAccount),
                              )}
                            </span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-muted-foreground">
                              Consecutive failures
                            </span>
                            <span className="font-medium">
                              {formatCount(
                                getConsecutiveFailures(selectedAccount),
                              )}
                            </span>
                          </div>
                        </div>

                        {getCooldownUntil(selectedAccount) && (
                          <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-600/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-950">
                            <Clock3 className="mt-0.5 size-3.5 shrink-0" />
                            <div>
                              <span className="font-medium">Cooldown: </span>
                              {formatTimestamp(
                                getCooldownUntil(selectedAccount),
                              )}
                            </div>
                          </div>
                        )}

                        {getLastError(selectedAccount) && (
                          <div className="mt-3 flex items-start gap-2 rounded-md border border-destructive/20 bg-destructive/5 px-3 py-2 text-xs text-destructive">
                            <ShieldAlert className="mt-0.5 size-3.5 shrink-0" />
                            <div className="min-w-0">
                              <span className="font-medium">Error: </span>
                              {getLastError(selectedAccount)}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent
          value="audit"
          className="m-0 min-h-0 flex-1 data-[state=inactive]:hidden"
        >
          <div className="space-y-4">
            {/* Audit Stats */}
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-lg border border-border/60 bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Success Rate
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {formatPercent(auditSummaryStats.successRatePct)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatCount(auditSummaryStats.successCount)} of{" "}
                  {formatCount(auditSummaryStats.selectionCount)}
                </p>
              </div>

              <div className="rounded-lg border border-border/60 bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  {TTS_POOL_METRIC_LABELS.failover}
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {formatCount(auditSummaryStats.failoverCount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  {formatCount(auditSummaryStats.beforeAudioFailureCount)}{" "}
                  failures
                </p>
              </div>

              <div className="rounded-lg border border-border/60 bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  {TTS_POOL_METRIC_LABELS.fallback}
                </p>
                <p className="mt-1 text-2xl font-semibold">
                  {formatCount(auditSummaryStats.fallbackToElevenlabsCount)}
                </p>
                <p className="text-xs text-muted-foreground">
                  To ElevenLabs
                </p>
              </div>

              <div className="rounded-lg border border-border/60 bg-background p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                  Provider Focus
                </p>
                <div className="mt-2">
                  <Select
                    value={selectedAuditProvider}
                    onValueChange={setSelectedAuditProvider}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="All providers" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL_PROVIDERS}>
                        All providers
                      </SelectItem>
                      {auditProviderOptions.map((provider) => (
                        <SelectItem key={provider} value={provider}>
                          {formatProviderLabel(provider)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            {/* Charts */}
            <div className="grid gap-4 xl:grid-cols-2">
              <Card className="overflow-hidden py-0">
                <CardHeader className="border-b border-border/60 px-4 py-3">
                  <CardTitle className="text-sm font-semibold">
                    Provider Split
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Cumulative requests, successful turns, and pre-audio failures by provider
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-4 py-4">
                  {providerAuditChartData.length > 0 ? (
                    <ChartContainer
                      config={auditOutcomeChartConfig}
                      className="aspect-auto h-[280px] w-full"
                    >
                      <BarChart data={providerAuditChartData} barGap={8}>
                        <CartesianGrid
                          strokeDasharray="3 3"
                          vertical={false}
                        />
                        <XAxis
                          dataKey="providerLabel"
                          tickLine={false}
                          axisLine={false}
                          tickMargin={8}
                          tick={{ fontSize: 11 }}
                        />
                        <YAxis
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 11 }}
                          allowDecimals={false}
                        />
                        <ChartTooltip
                          content={
                            <ChartTooltipContent
                              indicator="dashed"
                              nameKey="label"
                            />
                          }
                        />
                        <ChartLegend
                          content={
                            <ChartLegendContent className="flex-wrap justify-start gap-x-4 gap-y-2 pt-4 text-[11px] font-medium" />
                          }
                        />
                        <Bar
                          dataKey="selections"
                          fill="var(--color-selections)"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="successes"
                          fill="var(--color-successes)"
                          radius={[4, 4, 0, 0]}
                        />
                        <Bar
                          dataKey="failures"
                          fill="var(--color-failures)"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex h-[280px] items-center justify-center text-sm text-muted-foreground">
                      No provider metrics yet.
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card className="overflow-hidden py-0">
                <CardHeader className="border-b border-border/60 px-4 py-3">
                  <CardTitle className="text-sm font-semibold">
                    Key Performance
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Requests, successful turns, and pre-audio failures by account. Request share shown at row end.
                  </CardDescription>
                </CardHeader>
                <CardContent className="px-4 py-4">
                  {keySplitChartData.length > 0 ? (
                    <ChartContainer
                      config={auditOutcomeChartConfig}
                      className="aspect-auto w-full"
                      style={{
                        height: Math.max(280, keySplitChartData.length * 72),
                      }}
                    >
                      <BarChart
                        data={keySplitChartData}
                        layout="vertical"
                        barGap={4}
                        margin={{ top: 8, right: 64, left: 8 }}
                      >
                        <CartesianGrid
                          strokeDasharray="3 3"
                          horizontal={false}
                        />
                        <XAxis
                          type="number"
                          allowDecimals={false}
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 11 }}
                          tickFormatter={(value) =>
                            formatCount(Number(value))
                          }
                        />
                        <YAxis
                          type="category"
                          dataKey="label"
                          tickLine={false}
                          axisLine={false}
                          tick={{ fontSize: 10 }}
                          width={180}
                        />
                        <ChartTooltip
                          content={<KeyPerformanceTooltip />}
                        />
                        <ChartLegend
                          content={
                            <ChartLegendContent className="flex-wrap justify-start gap-x-4 gap-y-2 pt-4 text-[11px] font-medium" />
                          }
                        />
                        <Bar
                          dataKey="selections"
                          fill="var(--color-selections)"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={16}
                        >
                          <LabelList
                            dataKey="sharePct"
                            position="right"
                            content={<KeyPerformanceShareLabel />}
                          />
                        </Bar>
                        <Bar
                          dataKey="successes"
                          fill="var(--color-successes)"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={16}
                        />
                        <Bar
                          dataKey="failures"
                          fill="var(--color-failures)"
                          radius={[0, 4, 4, 0]}
                          maxBarSize={16}
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="flex h-[260px] items-center justify-center text-sm text-muted-foreground">
                      No account counters yet.
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
